import Big from 'big.js';
import { DateTime } from 'luxon';

import { APIConfig } from 'src/config';
import {
  BUY, CARBON, DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO,
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY,
  DATA_GROUP_BY_TRADE_TYPE, DIRECTIONS,
  PLATFORM_MODE_REBATE, SELL, SOURCE_HISTORIAN,
  SOURCE_TRADES, TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_CONTRACTED,
  TRADE_TYPE_NOMINATED, TRADE_TYPE_ORDER,
  TRADE_TYPE_RESIDUAL, TRADE_TYPE_UNSPECIFIED, TradeTypes, UNIT_CARBON, UNIT_CURRENCY,
  UNIT_ENERGY, VALUE, VOLUME, X_AXIS_DOMAIN_DYNAMIC, X_AXIS_DOMAIN_FIXED,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import logger from 'src/util/logger';
import { isNumber } from 'src/util/math';
import getTradeType from 'src/util/trade';

/**
 * Filters out falsy values from an object.
 * @param {object} obj - The object to filter
 * @returns {object} A new object with only truthy values
 */
export const getTruthyValues = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, value]) => value),
);

/**
 * Sums specific element in an object
 * @param {Array} data - tradeSetSummaries, untradedDataAggregates
 * @param {string} key  - value, volume, carbon
 * @returns {Big | NaN} Big number - sum of the specific element
 */
export const sumValuesByObjKeys = (data, key) => {
  if (!data) return NaN;

  return (data.reduce(
    (acc, element) => (element[key] ? acc.plus(element[key]) : acc),
    Big(0),
  ));
};

/**
 * Transforms the main data into the format required for the
 * trade summary component on the property dashboard.
 * @param {object} mainData
 * @returns {object} - trade summary
 */
export const getTradeSummary = (mainData) => {
  const finalResp = {
    buy: {},
    sell: {},
  };

  if (!mainData) return finalResp;

  const IS_MODE_REBATE = APIConfig().MODE === PLATFORM_MODE_REBATE;

  DIRECTIONS.forEach((dir) => {
    if (!mainData[dir]?.data) {
      return;
    }
    Object.keys(mainData[dir].data)?.forEach((timestamp) => {
      const {
        meterDataAggregates: meters,
        tradeSetSummaries: trades,
        untradedDataAggregates: untradedData,
      } = mainData[dir].data[timestamp] || {};
      if (!meters) {
        return;
      }
      const meterValue = sumValuesByObjKeys(Object.values(meters), VALUE);
      const untradedVolume = sumValuesByObjKeys(Object.values(untradedData), VOLUME);
      const untradedValue = sumValuesByObjKeys(Object.values(untradedData), VALUE);
      const energy = Number(meterValue) ? Big(meterValue) : Big(0);

      let tradedEnergy = Big(0);
      let tradedValue = Big(0);
      let totalResidual = Big(0);
      let rebatedEnergy = Big(0);
      let netDiffValue = Big(0);

      if (trades) {
        Object.keys(trades)?.forEach((tradeId) => {
          const {
            counterfactual, type, value = 0, volume = 0,
          } = trades[tradeId] || {};
          tradedEnergy = tradedEnergy.plus(volume);
          tradedValue = tradedValue.plus(value);
          const isResidual = type === TRADE_TYPE_RESIDUAL;
          if (isResidual) {
            netDiffValue = netDiffValue.plus(value);
          } else if (counterfactual && Number(counterfactual)) {
            netDiffValue = netDiffValue.plus(counterfactual);
          }
          if (IS_MODE_REBATE && isResidual) {
            totalResidual = totalResidual.plus(volume);
          }
        });
      }

      if (IS_MODE_REBATE) {
        rebatedEnergy = tradedEnergy.minus(totalResidual);
      }

      finalResp[dir][timestamp] = {
        energy: Number(energy),
        tradedEnergy: Number(tradedEnergy),
        tradedValue: Number(tradedValue),
        rebatedEnergy: Number(rebatedEnergy),
        netDiffValue: Number(netDiffValue),
        untradedVolume,
        untradedValue,
      };
    });
  });
  return finalResp;
};

/**
 * Transforms a main data set into the data set used in the Chart Summary component.
 * The key parts of the main data set is that it needs to have trade summaries.
 * @param {object} mainData
 * @returns {object} chart summary data, split into buy and sell directions.
 */
export const getChartSummaryData = (mainData) => {
  const finalResp = { buy: {}, sell: {} };

  if (!mainData) return finalResp;

  const tradeSummary = getTradeSummary(mainData);

  Object.keys(tradeSummary)?.forEach((dir) => {
    const dirSummary = {
      energy: Big(0),
      netDiffValue: Big(0),
      rebatedEnergy: Big(0),
      tradedEnergy: Big(0),
      tradedValue: Big(0),
      untradedCount: 0,
      untradedValue: Big(0),
      untradedVolume: Big(0),
    };

    Object.values(tradeSummary[dir])?.forEach((datum) => {
      const {
        energy, netDiffValue, rebatedEnergy,
        tradedEnergy, tradedValue, untradedValue, untradedVolume,
      } = datum;

      dirSummary.energy = dirSummary.energy.plus(energy);
      dirSummary.netDiffValue = dirSummary.netDiffValue.plus(netDiffValue);
      dirSummary.rebatedEnergy = dirSummary.rebatedEnergy.plus(rebatedEnergy);
      dirSummary.tradedEnergy = dirSummary.tradedEnergy.plus(tradedEnergy);
      dirSummary.tradedValue = dirSummary.tradedValue.plus(tradedValue);
      dirSummary.untradedValue = dirSummary.untradedValue.plus(untradedValue);
      dirSummary.untradedVolume = dirSummary.untradedVolume.plus(untradedVolume);

      if (Number(untradedVolume) > 0) {
        dirSummary.untradedCount += 1;
      }
    });

    finalResp[dir] = {
      energy: Number(dirSummary.energy),
      netDiffValue: Number(dirSummary.netDiffValue),
      rebatedEnergy: Number(dirSummary.rebatedEnergy),
      tradedEnergy: Number(dirSummary.tradedEnergy),
      tradedValue: Number(dirSummary.tradedValue),
      untradedCount: Number(dirSummary.untradedCount),
      untradedValue: Number(dirSummary.untradedValue),
      untradedVolume: Number(dirSummary.untradedVolume),
    };
  });

  return finalResp;
};

/**
 * Converts a timestamp to an ISO string representation in UTC.
 * @param {string} timestamp - The timestamp to convert, in milliseconds.
 * @returns {string} - The timestamp as an ISO string in UTC.
 */
export const timestampToIsoString = (timestamp) => {
  const ts = DateTime.fromMillis(parseFloat(timestamp));
  return ts.toUTC().toString();
};

/**
 * Returns the correct counter party based on the trade type and direction
 * @param {object} rule - trade rule
 * @param {TRADE_TYPE_CONTRACTED | TRADE_TYPE_NOMINATED|
 * TRADE_TYPE_COMMUNITY| TRADE_TYPE_RESIDUAL} type - type of trade
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} direction - direction of trade
 * @returns {object | null} - buyer, seller, null
 */
export const getCounterParty = (rule, type, direction) => {
  if (!rule || !type || !direction) return null;

  if (type !== TRADE_TYPE_CONTRACTED && type !== TRADE_TYPE_NOMINATED) return null;

  return direction === TRADE_DIRECTION_BUY ? rule.seller : rule.buyer;
};

/**
 * Returns the trade direction based on the given direction.
 * @param {BUY | SELL} direction - The direction of the trade.
 * @returns {TRADE_DIRECTION_UNSPECIFIED |TRADE_DIRECTION_BUY |
 *  TRADE_DIRECTION_SELL } - The trade direction.
 */
export const getDirection = (direction) => {
  switch (direction) {
    case BUY:
      return TRADE_DIRECTION_BUY;
    case SELL:
      return TRADE_DIRECTION_SELL;
    default:
      return TRADE_DIRECTION_UNSPECIFIED;
  }
};

/**
 * Retrieves the property associated with a trader.
 * @param {object} trader - The trader object.
 * @returns {object | null} - The property object if found, otherwise null.
 */
export const getTraderProperty = (trader) => (trader?.tradePoint?.meter?.property || null);

/**
 * Get the entity id, falling to the default value in its absence.
 * @param {object} entity
 * @param {string} defaultIdValue
 * @returns {string} - entity id.
 */
export const getEntityId = (entity, defaultIdValue = null) => (entity?.uuid || defaultIdValue);

/**
 * Builds chart meter data for aggregated view.
 * @param {object} mainData - chart primary data.
 * @param {string} timezone
 * @returns {object} - meter data (timestamp based).
 */
export const buildChartMeterDataAggregated = (mainData, timezone) => {
  const finalResp = {};
  if (!mainData) return finalResp;
  let value = Big(0);
  let carbon = Big(0);

  DIRECTIONS.forEach((dir) => {
    finalResp[dir] = {};
    let flags = [];
    if (mainData[dir]) {
      Object.keys(mainData[dir].data).forEach((timestamp) => {
        const { meterDataAggregates } = mainData[dir].data[timestamp];
        if (Object.keys(meterDataAggregates).length === 1) {
          const meterId = Object.keys(meterDataAggregates)[0];
          carbon = Big(meterDataAggregates[meterId].carbon);
          value = meterDataAggregates[meterId].value;
          flags = meterDataAggregates[meterId].flags;
        }
        if (Object.keys(meterDataAggregates).length > 1) {
          value = sumValuesByObjKeys(Object.values(meterDataAggregates), VALUE);
          carbon = sumValuesByObjKeys(Object.values(meterDataAggregates), CARBON);
          Object.keys(meterDataAggregates).forEach((meterId) => {
            meterDataAggregates[meterId].flags.forEach((flag) => {
              if (flags.map((f) => f.identifier).indexOf(flag.identifier) === -1) {
                flags.push(flag);
              }
            });
          });
        }
        const formattedTimestamp = timestampToIsoString(timestamp);
        const meterDataSeries = {
          timestamp: DateTime.fromISO(formattedTimestamp, { zone: timezone }),
          value: Number(value),
          flags,
          carbon,
        };
        finalResp[dir][formattedTimestamp] = meterDataSeries;
      });
    }
  });

  return finalResp;
};

/**
 * Returns the buy/sell aggregated values and volumes of the untraded energy from main data.
 * @param {object} mainData
 * @returns {{
 * buy: {volume: number, value: number},
 * sell: {volume: number, value: number}
 * } | object} The untraded object.
 */
export const getUntradedTotals = (mainData) => {
  const tradeSummary = getTradeSummary(mainData);
  const finalResp = { buy: {}, sell: {} };

  Object.keys(tradeSummary).forEach((dir) => {
    finalResp[dir].value = Big(0);
    finalResp[dir].volume = Big(0);
    const finalIndex = Object.keys(tradeSummary[dir]).length - 1;

    Object.keys(tradeSummary[dir]).forEach((timestamp, index) => {
      const { untradedValue: value, untradedVolume: volume } = tradeSummary[dir][timestamp];
      const aggregatedValue = finalResp[dir].value.plus(value);
      const aggregatedVolume = finalResp[dir].volume.plus(volume);
      finalResp[dir].value = (index === finalIndex) ? Number(aggregatedValue) : aggregatedValue;
      finalResp[dir].volume = (index === finalIndex) ? Number(aggregatedVolume) : aggregatedVolume;
    });
  });

  return finalResp;
};

/**
 * Prepares the trade summary data, from a given input (mainData).
 * @param {object} mainData
 * @returns {object} an opinionated data structure for use in the trade summary on
 * the property show page.
 */
export const buildTradeSummaryData = (mainData) => {
  const resp = {
    buy: {
      contracted: [],
      nominated: [],
      community: [],
      residual: [],
      untraded: [],
    },
    sell: {
      contracted: [],
      nominated: [],
      community: [],
      residual: [],
      untraded: [],
    },
  };

  if (!mainData) {
    return resp;
  }

  const trades = {
    buy: {}, sell: {},
  };

  DIRECTIONS.forEach((dir) => {
    const { data, rules } = mainData[dir];
    if (data) {
      const tradeSetSummaries = Object.keys(data)?.map(
        (timestamp) => data[timestamp].tradeSetSummaries,
      ).filter((item) => item);
      if (tradeSetSummaries?.length === 0) {
        return;
      }
      tradeSetSummaries?.forEach((tradeSet) => {
        Object.keys(tradeSet)?.forEach((ruleId) => {
          const {
            counterfactual, value, volume,
          } = tradeSet[ruleId];
          const finalCounterfactual = isNumber(counterfactual) ? counterfactual : 0;
          if (trades[dir][ruleId]) {
            const {
              value: tradeValue, volume: tradeVolume,
              counterfactual: tradeCounterfactual,
            } = trades[dir][ruleId];
            trades[dir][ruleId] = {
              ...trades[dir][ruleId],
              value: Number(Big(tradeValue).plus(value)),
              volume: Number(Big(tradeVolume).plus(volume)),
              counterfactual: Number(Big(tradeCounterfactual).plus(finalCounterfactual)),
            };
          } else {
            const rule = rules[ruleId];
            trades[dir][ruleId] = {
              value, volume, counterfactual: finalCounterfactual, rule,
            };
          }
        });
      });
    }
    TradeTypes.forEach((tradeType) => {
      const typeKey = getTradeType(tradeType);
      Object.keys(trades[dir])?.forEach((tradeRuleId) => {
        const type = trades[dir][tradeRuleId]?.rule?.tradeType;
        if (type === tradeType) {
          resp[dir][typeKey].push(trades[dir][tradeRuleId]);
        }
      });
    });
  });

  const untraded = getUntradedTotals(mainData);
  if (untraded?.buy?.volume > 0) {
    resp.buy.untraded.push(untraded.buy);
  }

  if (untraded?.sell?.volume > 0) {
    resp.sell.untraded.push(untraded.sell);
  }
  return resp;
};

/**
 * Converts the input to a number
 * We are using this function extensively for display purposes across the
 * property and portfolio dashboard where the default would be 0
 * @param {any} x
 * @param {object} [opts = {default: undefined}] - optional options arg
 @returns {number|NaN} - Returns the converted number
 */
export const convertToNumber = (x, opts = { default: undefined }) => {
  const y = Number(x);

  // If the input is a number return the input
  if (!Number.isNaN(y)) { return y; }

  const defaultValue = opts?.default;

  // if the input is NaN return the default value (if available) or the input (y)
  return defaultValue !== undefined ? defaultValue : y;
};

/**
 * Checks whether an object have untraded data with a volume greater than zero.
 * @param {object} data - The timeseries data object.
 * @returns {boolean} - Returns true if there is a volume different from zero, otherwise false.
 */
export const hasVolume = (data) => {
  if (!data) {
    return false;
  }

  return Object.values(data).some(({ volume }) => volume !== 0);
};

/**
 * Sorts in descending order based on the sum of `buy.volume` and `sell.volume`.
 * If volumes are equal, it sorts by `key` in ascending order.
 * @param {object} a - meter object a
 * @param {object} b - meter object b
 * @returns {number} - sort order
 */
export const byAggregatedVolumeFunc = (a, b) => {
  const aVolume = convertToNumber(a.buy.volume) + convertToNumber(a.sell.volume);
  const bVolume = convertToNumber(b.buy.volume) + convertToNumber(b.sell.volume);

  if (aVolume !== bVolume) {
    return bVolume - aVolume;
  }

  return a.key > b.key ? 1 : -1;
};

/**
 * Sorts in descending order based on the sum of `buy.volume` and `sell.volume`.
 * If volumes are equal, it sorts by `key` in ascending order.
 * @param {object} a - meter object a
 * @param {object} b - meter object b
 * @returns {number} - sort order
 */
export const byTradeTypeAndAggregatedVolumeFunc = (a, b) => {
  const orderB = TRADE_TYPE_ORDER.indexOf(b.tradeType);
  const orderA = TRADE_TYPE_ORDER.indexOf(a.tradeType);

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  return byAggregatedVolumeFunc(a, b);
};

/**
 * Build a key, given a parent ID and the chart view object providing aggregate details.
 * @param {string} parentId The ID of the parent.
 * @param {object} chartView The chart view configuration.
 * @param {DATA_AGGREGATE_BY_METER | DATA_AGGREGATE_BY_PROPERTY
 * | DATA_AGGREGATE_BY_PORTFOLIO} chartView.aggregateBy
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 * @param {string} tradeType the type of trade.
 * @param {object} opts additional options.
 * @returns {string} A key compounding the parent ID and the aggregate by.
 */
export const buildTradeViewKey = (parentId, chartView, tradeType, opts = {}) => {
  const { aggregateBy, groupBy } = chartView || {};
  const { counterParty, counterPartyProperty } = opts || {};

  if (!tradeType) {
    return TRADE_TYPE_UNSPECIFIED;
  }

  // Community and residuals are just the parent and the trade type.
  if ([TRADE_TYPE_COMMUNITY, TRADE_TYPE_RESIDUAL].indexOf(tradeType) !== -1) {
    return [parentId, tradeType, aggregateBy, groupBy].filter(Boolean).join('_');
  }

  switch (groupBy) {
    case DATA_GROUP_BY_COUNTERPARTY: {
      const counterPartyUserId = getEntityId(counterParty?.user);
      const counterPartyPropertyId = getEntityId(counterPartyProperty);
      return [parentId, tradeType, counterPartyUserId || '', counterPartyPropertyId || '', aggregateBy, groupBy].filter(Boolean).join('_');
    }
    case DATA_GROUP_BY_TRADE_TYPE:
      return [parentId, tradeType, aggregateBy, groupBy].filter(Boolean).join('_');
    default:
      logger.error(`group by '${groupBy}' are not valid for buildTradeViewKey`);
      return TRADE_TYPE_UNSPECIFIED;
  }
};

/**
 * Build a chart view key that includes all properties of chartView and source.
 * @param {object} chartView The chart view configuration.
 * @param {string} source The data source.
 * @returns {string|null} A key compounding all chartView properties and source,
 *   or null if invalid parameters.
 */
export const buildChartViewKey = (chartView, source) => {
  // Check for null/undefined/falsy parameters
  if (!chartView || !source) {
    return null;
  }

  // Validate chartView is an object (not array, function, etc.)
  if (typeof chartView !== 'object' || Array.isArray(chartView)) {
    return null;
  }

  return `${Object.entries(chartView)
    .map(([key, value]) => `${key}_${value}`)
    .join('_')}_source_${source}`;
};

/**
 * Toggles a key in a chart view's selected keys array within a selectedKeys state object.
 * @param {object} currentlySelectedKeys - Current selectedKeys state object where keys are
 * chart view identifiers (strings) and values are arrays of selected keys for that chart view.
 *   Example: { "chartView1": ["key1", "key2"], "chartView2": ["keyA"] }
 * see src/components/Dashboard/helpers/common.test.js#buildChartViewKey for specific examples.
 * @param {string} chartViewKey - The chart view identifier
 * @param {string} key - The key to toggle
 * @returns {object} Updated selectedKeys object with the key toggled, or original selectedKeys
 *   if invalid inputs
 * @example
 * // Toggle "key1" in "chartView1"
 * const currentState = { "chartView1": ["key2"], "chartView2": ["keyA"] };
 * const result = selectedKeysToggleKey(currentState, "chartView1", "key1");
 * // Returns: { "chartView1": ["key2", "key1"], "chartView2": ["keyA"] }
 */
export const selectedKeysToggleKey = (currentlySelectedKeys, chartViewKey, key) => {
  // Parameter validation - return original selectedKeys for invalid inputs
  if (!currentlySelectedKeys) {
    return {};
  }

  // Validate chartViewKey is a valid string
  if (!chartViewKey) {
    return { ...currentlySelectedKeys };
  }

  const newSelectedKeys = { ...currentlySelectedKeys };

  // Initialize the array for this chart view if it doesn't exist
  if (!newSelectedKeys[chartViewKey]) {
    newSelectedKeys[chartViewKey] = [];
  }

  // Get the current keys for this chart view
  const chartViewKeys = [...(newSelectedKeys[chartViewKey] || [])];

  // Toggle the key in the array
  if (chartViewKeys.includes(key)) {
    // Remove the key if it already exists
    const keyIndex = chartViewKeys.indexOf(key);
    chartViewKeys.splice(keyIndex, 1);
  } else {
    // Add the key if it doesn't exist
    chartViewKeys.push(key);
  }

  // Update the state with the new array for this chart view
  newSelectedKeys[chartViewKey] = chartViewKeys;

  return newSelectedKeys;
};

/**
 * Calculates selection state information for chart components.
 * @param {object} selectedKeys - The selectedKeys state object
 * @param {string} chartViewKey - The current chart view identifier
 * @param {Array} cardsData - The cards data array
 * @returns {object} Selection state information
 */
export const getCardSelectionState = (selectedKeys, chartViewKey, cardsData) => {
  const finalSelectedKeys = chartViewKey && selectedKeys[chartViewKey]
    ? selectedKeys[chartViewKey] : [];

  const hasCurrentViewSelectedKeys = chartViewKey
    && selectedKeys[chartViewKey]
    && selectedKeys[chartViewKey].length > 0;

  const hasAnySelectedKeys = Object.values(selectedKeys)
    .some((value) => Array.isArray(value) && value.length > 0);

  const enableClearBtn = hasCurrentViewSelectedKeys || hasAnySelectedKeys;
  const cardsDataAvailable = cardsData.length > 0;

  return {
    finalSelectedKeys,
    hasCurrentViewSelectedKeys,
    hasAnySelectedKeys,
    enableClearBtn,
    cardsDataAvailable,
  };
};

/**
 * Constructs URL state parameters for chart views in the property and portfolio dashboards.
 * This function builds the state object used for URL navigation and browser history,
 * combining time range parameters with optional chart view configuration.
 * @param {object} chartView - Chart view configuration object containing display settings
 * @param {DATA_AGGREGATE_BY_PROPERTY | DATA_AGGREGATE_BY_METER} chartView.aggregateBy
 *   How data should be aggregated (by property or by individual meters)
 * @param {DATA_GROUP_BY_COUNTERPARTY | DATA_GROUP_BY_TRADE_TYPE} chartView.groupBy
 *   How data should be grouped in the chart display
 * @param {X_AXIS_DOMAIN_FIXED | X_AXIS_DOMAIN_DYNAMIC} chartView.xAxisDomain
 *   X-axis domain configuration for chart scaling
 * @param {DateTime} startTime - Luxon DateTime object representing the start of the time range
 * @param {DateTime} finishTime - Luxon DateTime object representing the end of the time range
 * @param {string} timezone - IANA timezone identifier (e.g., 'Australia/Sydney')
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} source - Data source identifier
 * @param {UNIT_CARBON | UNIT_CURRENCY | UNIT_ENERGY} unit - Chart display unit
 * @param {object} selectedKeys - The selectedKeys state object
 * @returns {object | null} URL state object containing:
 *   - Base state (always included): { start: string, finish: string, tz: string }
 *   - Optional view parameters (when includeViewParams is true):
 *     { aggregateBy, groupBy, xAxisDomain, source, unit }
 */
export const buildStateURLParams = (
  chartView,
  startTime,
  finishTime,
  timezone,
  source,
  unit,
  selectedKeys,
) => {
  if (!chartView || !startTime || !finishTime || !timezone || !source || !unit) return null;
  const { aggregateBy, groupBy, xAxisDomain } = chartView;
  const baseState = {
    start: startTime.toISODate(),
    finish: finishTime.toISODate(),
    tz: timezone,
  };

  // Build the chart view key for the current view
  const chartViewKey = buildChartViewKey(chartView, source);

  // Get selectedKeys for the current view only
  const currentViewSelectedKeys = chartViewKey && selectedKeys && selectedKeys[chartViewKey]
    ? selectedKeys[chartViewKey]
    : [];

  // Serialize selectedKeys as comma-separated string (human-readable)
  const selectedKeysParam = currentViewSelectedKeys.length > 0
    ? currentViewSelectedKeys.join(',')
    : null;

  return {
    ...baseState,
    ...getTruthyValues({
      aggregateBy,
      groupBy,
      xAxisDomain,
      source,
      unit,
      cardSelectedKeys: selectedKeysParam,
    }),
  };
};

/**
 * Constructs URL state for dashboard navigation with time range and chart view parameters.
 * @param {object} datespan - Date range object containing start and finish DateTime properties
 * @param {object} match - React Router match object with location information
 * @param {object} chartView - Chart view configuration with aggregateBy, groupBy, xAxisDomain
 * @param {string} timezone - IANA timezone identifier for date formatting
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} source - Data source identifier
 * @param {UNIT_CARBON | UNIT_CURRENCY | UNIT_ENERGY} unit - Chart display unit
 * @param {object} selectedKeys - The selectedKeys state object
 * @returns {object|null} URL state object with {pathname, searchString, state} properties,
 *   or null when URL state generation fails
 */
export const buildEntityShowState = (
  datespan,
  match,
  chartView,
  timezone,
  source,
  unit,
  selectedKeys,
) => {
  if (!datespan || !match?.location) return null;
  const { start: startTime, finish: finishTime } = datespan;
  const { location } = match;
  const { pathname } = location;
  const state = buildStateURLParams(
    chartView,
    startTime,
    finishTime,
    timezone,
    source,
    unit,
    selectedKeys,
  );

  if (!state) {
    logger.warn('Failed to generate URL state, skipping update');
    return null;
  }
  const searchString = `?${Object.keys(state)
    .map((k) => `${k}=${encodeURIComponent(state[k])}`)
    .join('&')}`;

  return { pathname, searchString, state };
};

/**
 * Updates the browser URL for dashboard state without triggering a page reload.
 * Performs an early return if urlState is null/undefined or if the searchString
 * matches the current URL to avoid unnecessary navigation.
 * @param {object} urlState - URL state object containing navigation parameters
 * @param {string} urlState.pathname - The URL pathname
 * @param {string} urlState.searchString - The URL search string with query parameters
 * @param {object} urlState.state - The browser history state object
 */
export const navigateToEntityShowState = (urlState) => {
  if (!urlState) {
    return;
  }
  const { pathname, searchString, state } = urlState;

  // Validate pathname before using it as a navigation target. A missing or
  // empty pathname indicates a malformed urlState (e.g. a match.location with
  // no pathname), which is a caller bug — log a warning so it surfaces in
  // monitoring rather than silently dropping the update.
  if (!pathname) {
    logger.warn('navigateToEntityShowState: pathname is missing or empty, skipping URL update', { urlState });
    return;
  }

  // Avoid unnecessary updates
  if (window.location.search === searchString) {
    return;
  }
  BrowserProtocol.navigate({
    action: 'REPLACE',
    pathname,
    search: searchString,
    hash: '',
    state,
  });
};

/**
 * Updates the browser URL with dashboard state parameters.
 * @param {object} datespan - Date range object with start and finish DateTime properties
 * @param {object} match - React Router match object containing location information
 * @param {object} chartView - Chart view configuration object with aggregateBy, groupBy,
 *   and xAxisDomain properties
 * @param {string} timezone - IANA timezone identifier
 * @param {SOURCE_HISTORIAN | SOURCE_TRADES} source - Data source identifier
 * @param {UNIT_CARBON | UNIT_CURRENCY | UNIT_ENERGY} unit - Chart display unit
 * @param {object} selectedKeys - The selectedKeys state object
 */
export const updateEntityShowState = (
  datespan,
  match,
  chartView,
  timezone,
  source,
  unit,
  selectedKeys,
) => {
  if (!datespan || !match || !chartView || !timezone || !source || !unit) {
    logger.error('Invalid parameters for updateEntityShowState');
    return;
  }

  const urlState = buildEntityShowState(
    datespan,
    match,
    chartView,
    timezone,
    source,
    unit,
    selectedKeys,
  );
  navigateToEntityShowState(urlState);
};
